import React, { useState } from "react";
import PageIntro from "../components/PageIntro";
import PublicCautionNoticeData from "../content/publicCautionNotice.json";

const PublicCautionNotice = () => {
  const [language, setLanguage] = useState("english");
  const [isTransitioning, setIsTransitioning] = useState(false);

  const languageKey = language.charAt(0).toUpperCase() + language.slice(1);

  const currentData = PublicCautionNoticeData.disclaimer[languageKey];

  // HANDLE LANGUAGE CHANGE WITH ANIMATION
  const handleLanguageChange = (lang) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setLanguage(lang.toLowerCase());
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <>
      {/* PAGE INTRO */}
      <PageIntro
        title={PublicCautionNoticeData.title}
        subtitle=""
        compactBottom
        contentWidthClass="max-w-[1400px]"
        bottomPadding="pb-4 md:pb-6"
      />

      {/* MAIN SECTION */}
      <section className="bg-gradient-to-b from-slate-50 to-slate-100 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* CARD WITH TRANSITION ANIMATION */}
          <div
            className={`bg-white rounded-lg shadow-lg p-8 md:p-12 transition-all duration-300 ${
              isTransitioning ? "opacity-50 scale-98" : "opacity-100 scale-100"
            }`}
          >
            {/* LANGUAGE SELECTOR */}
            <div className="flex flex-wrap gap-4 justify-center mb-10 pb-6 border-b border-[#1c3d72]/10">
              {Object.keys(PublicCautionNoticeData.disclaimer).map((lang) => {
                const isActive = language === lang.toLowerCase();

                return (
                  <button
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className={`px-4 pb-2 text-sm md:text-base font-semibold transition-all duration-300 relative ${
                      isActive
                        ? "text-[#1c3d72] scale-105"
                        : "text-slate-600 hover:text-slate-900 hover:scale-102"
                    }`}
                  >
                    {lang}
                    {isActive && (
                      <span className="absolute -bottom-1 left-0 right-0 h-1 bg-[#1c3d72] rounded-full"></span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* DISCLAIMER TEXT */}
            <div className="text-slate-700 space-y-4 mb-12">
              {currentData.text.map((paragraph, idx) => (
                <div
                  key={idx}
                  className={`transition-all duration-500 ${
                    isTransitioning
                      ? "opacity-0 translate-y-4"
                      : "opacity-100 translate-y-0"
                  }`}
                  style={{
                    transitionDelay: isTransitioning ? "0ms" : `${idx * 50}ms`,
                  }}
                >
                  <p className="text-base md:text-lg leading-relaxed">
                    {paragraph}
                  </p>
                </div>
              ))}
            </div>

            {/* DIVIDER */}
            <div className="border-t border-[#1c3d72]/10 my-10" />

            {/* COMPANY INFO */}
            <div className="grid md:grid-cols-2 gap-10">
              {/* LEFT */}
              <div className="space-y-6">
                <InfoBlock
                  label="Company"
                  isTransitioning={isTransitioning}
                  delay={0}
                >
                  {currentData.company}
                </InfoBlock>

                <InfoBlock
                  label="Address"
                  isTransitioning={isTransitioning}
                  delay={100}
                >
                  {currentData.address.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </InfoBlock>

                <InfoBlock
                  label="Phone"
                  isTransitioning={isTransitioning}
                  delay={200}
                >
                  <a
                    href={`tel:${currentData.phone}`}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {currentData.phone}
                  </a>
                </InfoBlock>
              </div>

              {/* RIGHT */}
              <div className="space-y-6">
                <InfoBlock
                  label="Email"
                  isTransitioning={isTransitioning}
                  delay={0}
                >
                  <a
                    href={`mailto:${currentData.email}`}
                    className="text-blue-600 hover:text-blue-800 break-all transition-colors"
                  >
                    {currentData.email}
                  </a>
                </InfoBlock>
                <InfoBlock
                  label="Website"
                  isTransitioning={isTransitioning}
                  delay={100}
                >
                  <a
                    href={`https://${currentData.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {currentData.website}
                  </a>
                </InfoBlock>

                <InfoBlock
                  label="CIN"
                  isTransitioning={isTransitioning}
                  delay={200}
                >
                  <span className="font-mono break-all">{currentData.cin}</span>
                </InfoBlock>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slideDown {
          animation: slideDown 0.6s ease-out;
        }

        .scale-98 {
          transform: scale(0.98);
        }

        .scale-102 {
          transform: scale(1.02);
        }
      `}</style>
    </>
  );
};

/* SMALL REUSABLE COMPONENT WITH ANIMATION */
const InfoBlock = ({ label, children, isTransitioning, delay }) => (
  <div
    className={`transition-all duration-500 ${
      isTransitioning ? "opacity-0 -translate-x-4" : "opacity-100 translate-x-0"
    }`}
    style={{
      transitionDelay: isTransitioning ? "0ms" : `${delay}ms`,
    }}
  >
    <p className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-1">
      {label}
    </p>
    <div className="text-base text-slate-900">{children}</div>
  </div>
);

export default PublicCautionNotice;
